import {
    MouseEvent, useCallback, useMemo, useRef,
} from 'react';
import { DateTime } from 'luxon';
import { useLocale } from 'react-rainbow-components';
import Gradient from 'components/Gradient';
import { Calendarevent } from 'data/firestore/agent/calendarevent/types';
import { Calendar } from 'data/firestore/agent/calendar/types';
import { CalendarEventClick } from 'types';
import Hours from '../CalendarDayView/hours';
import getMinuteFromPos from '../CalendarDayView/calendars/helpers/getMinuteFromPos';
import Days from './days';
import Header from './header';
import getAvailabilityForWeekDay from './helpers/getAvailabilityForWeekDay';
import getCollapsedDays from './helpers/getCollapsedDays';
import getWeekDays from './helpers/getWeekDays';
import groupEventsByDay from './helpers/groupEventsByDay';
import { Container, Content } from './styled';

interface CalendarWeekViewProps {
    className?: string;
    locale?: string;
    calendars?: Calendar[];
    events?: Calendarevent[];
    date?: Date;
    timeZone?: string;
    selectedEvent?: string | null;
    onEventClick?: CalendarEventClick;
    onAvailableSpaceClick?: (day: Date, minute: number) => void;
}

const CalendarWeekView = ({
    className,
    locale: localLocale,
    calendars = [],
    events = [],
    date = new Date(),
    timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone,
    selectedEvent,
    onEventClick = () => {},
    onAvailableSpaceClick = () => {},
}: CalendarWeekViewProps) => {
    const locale = useLocale(localLocale);
    const hoursRef = useRef<HTMLDivElement>(null);
    const daysRef = useRef<HTMLDivElement>(null);

    const days = useMemo(
        () => getWeekDays({ date, timeZone }),
        [date, timeZone],
    );

    const groupedDays = useMemo(
        () => groupEventsByDay({ events, days, timeZone }),
        [events, days, timeZone],
    );

    const availabilityPerDay = useMemo(
        () => days.map((day) => getAvailabilityForWeekDay({ calendars, day, timeZone })),
        [calendars, days, timeZone],
    );

    const collapsedDays = useMemo(
        () => getCollapsedDays(availabilityPerDay),
        [availabilityPerDay],
    );

    const dayColumns = useMemo(
        () => groupedDays.map(({ day, events: dayEvents }, index) => ({
            day,
            events: dayEvents,
            availability: availabilityPerDay[index],
            isCollapsed: collapsedDays[index],
        })),
        [availabilityPerDay, collapsedDays, groupedDays],
    );

    const todayIndex = useMemo(
        () => {
            const today = DateTime.now().setZone(timeZone);
            return days.findIndex((day) => (
                DateTime.fromJSDate(day).setZone(timeZone).hasSame(today, 'day')
            ));
        },
        [days, timeZone],
    );

    const handleScroll = useCallback(
        (event: any) => {
            if (hoursRef.current) {
                hoursRef.current.scrollTop = event.target.scrollTop;
            }
            if (daysRef.current) {
                daysRef.current.scrollLeft = event.target.scrollLeft;
            }
        },
        [],
    );

    const handleAvailableSpaceClick = useCallback(
        (day: Date, e: MouseEvent<HTMLDivElement>) => {
            if (hoursRef.current) {
                const { clientY } = e;
                const { top } = hoursRef.current.getBoundingClientRect();
                const { scrollTop } = hoursRef.current;
                const pos = clientY - top + scrollTop;
                const minute = getMinuteFromPos(pos);
                onAvailableSpaceClick(day, minute);
            }
        },
        [onAvailableSpaceClick],
    );

    return (
        <Container className={className}>
            <Header
                days={days}
                collapsedDays={collapsedDays}
                calendars={calendars}
                timeZone={timeZone}
                locale={locale}
                ref={daysRef}
            />
            <Content>
                <Hours
                    ref={hoursRef}
                    locale={locale}
                />
                <Days
                    days={dayColumns}
                    calendars={calendars}
                    selectedEvent={selectedEvent}
                    todayIndex={todayIndex}
                    onScroll={handleScroll}
                    onEventClick={onEventClick}
                    onAvailableSpaceClick={handleAvailableSpaceClick}
                    hideClockLine={todayIndex === -1}
                    timeZone={timeZone}
                />
            </Content>
            <Gradient />
        </Container>
    );
};

export default CalendarWeekView;
